/**
 * Payment Approval View Component
 * Task 7: Tuo's Payment Workflow (2nd Approver)
 * Framework C.4: Final sign-off before WBC release
 * Admin-only view accessed via `/payment-approval` route
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { wbcService } from '../services/wbcService';
import { auditService } from '../services/auditService';
import { User } from '../types';
import './PaymentApprovalView.css';
import PaymentHoldControls from './PaymentHoldControls';

interface PaymentRun {
  id: string;
  deal_id: string;
  purchase_price: number;
  holding_deposit: number;
  total_amount: number;
  winnie_review_complete: boolean;
  winnie_reviewed_at?: string;
  winnie_user_id?: string;
  initiated_by_user_id?: string;
  tuo_review_complete: boolean;
  tuo_reviewed_at?: string;
  status: string;
  wbc_batch_id?: string;
  payment_status?: string;
  deal?: {
    id: string;
    user_id?: string;
    autograb_year: string;
    autograb_make: string;
    autograb_model: string;
    buyer_name: string;
    seller_name: string;
  };
  winnie_profile?: {
    full_name: string;
  };
}

interface PaymentApprovalViewProps {
  user: User;
  paymentRunId?: string;
  onApprovalComplete?: (paymentRunId: string) => void;
}

export const PaymentApprovalView: React.FC<PaymentApprovalViewProps> = ({
  user,
  paymentRunId,
  onApprovalComplete
}) => {
  const [paymentRun, setPaymentRun] = useState<PaymentRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [manualOverride, setManualOverride] = useState(false);
  const [confirmRelease, setConfirmRelease] = useState<'wbc' | 'manual' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [wbcReference, setWbcReference] = useState('');

  useEffect(() => {
    loadPaymentRun();
  }, [paymentRunId]);

  const loadPaymentRun = async () => {
    try {
      setLoading(true);
      setError(null);
      setManualOverride(false);
      setWbcReference('');

      // Determine which payment run to load
      let query = supabase
        .from('payment_runs')
        .select(`
          *,
          deal:deals!deal_id(id, user_id, autograb_year, autograb_make, autograb_model, buyer_name, seller_name),
          winnie_profile:user_profiles!winnie_user_id(full_name),
          initiator_profile:user_profiles!initiated_by_user_id(full_name)
        `)
        .eq('status', 'pending_tuo')
        .eq('winnie_review_complete', true);

      if (paymentRunId) {
        query = query.eq('id', paymentRunId);
      }

      const { data, error: err } = await query.order('created_at', { ascending: true }).limit(1);

      if (err) throw err;
      if (!data || data.length === 0) {
        setPaymentRun(null);
        setError('No pending approvals');
        return;
      }

      setPaymentRun(data[0]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load payment run';
      setError(msg);
      console.error('Payment run load error:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Releasing money used to be a single unguarded click, while sending a reminder
   * email or an SMS both required an explicit confirmation. This puts the heaviest
   * action behind the strongest confirmation.
   */
  const handleApprove = async (useWBC: boolean = true) => {
    if (!paymentRun) return;

    try {
      setSubmitting(true);
      setError(null);

      // Get current user
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('User not authenticated');

      // Segregation of duties (Framework D.2): the 2nd releaser cannot be the deal
      // creator, and cannot be the same person as the 1st approver.
      if (paymentRun.deal?.user_id && paymentRun.deal.user_id === authUser.id) {
        await auditService.log(user, 'segregation_block', {
          details: { stage: '2nd_release', reason: 'creator_cannot_release', payment_run: paymentRun.id },
        });
        setError('Segregation of duties: you created this deal, so you cannot release its payment.');
        return;
      }
      if (paymentRun.winnie_user_id && paymentRun.winnie_user_id === authUser.id) {
        await auditService.log(user, 'segregation_block', {
          details: { stage: '2nd_release', reason: '1st_and_2nd_must_differ', payment_run: paymentRun.id },
        });
        setError('Segregation of duties: the 1st and 2nd approver must be different users.');
        return;
      }
      // The screen promises "Initiator ≠ 1st approver ≠ 2nd releaser" but only two
      // of the three were ever checked — initiated_by_user_id was written and read
      // by nothing.
      if (paymentRun.initiated_by_user_id && paymentRun.initiated_by_user_id === authUser.id) {
        await auditService.log(user, 'segregation_block', {
          details: { stage: '2nd_release', reason: 'initiator_cannot_release', payment_run: paymentRun.id },
        });
        setError('Segregation of duties: you initiated this payment run, so you cannot release it.');
        return;
      }

      // Persist Tuo's approval BEFORE releasing to WBC, so the WBC release
      // (which requires both winnie_review_complete AND tuo_review_complete)
      // sees this approval on the payment run.
      const { error: tuoError } = await supabase
        .from('payment_runs')
        .update({
          tuo_review_complete: true,
          tuo_reviewed_at: new Date().toISOString(),
          tuo_user_id: authUser.id
        })
        .eq('id', paymentRun.id);
      if (tuoError) throw tuoError;

      let updateData: any = {};

      if (useWBC) {
        // Framework C.4: Release to WBC
        const wbcResult = await wbcService.releasePaymentBatch(paymentRun.id);

        if (!wbcResult.success) {
          throw new Error(wbcResult.message);
        }

        updateData = {
          ...updateData,
          status: 'completed',
          wbc_batch_id: wbcResult.details?.batch_id
        };
      } else {
        // Manual override
        if (!wbcReference.trim()) {
          setError('WBC reference number required for manual override');
          setSubmitting(false);
          return;
        }

        const overrideResult = await wbcService.manualPaymentOverride(
          paymentRun.id,
          wbcReference,
          user.id
        );

        if (!overrideResult.success) {
          throw new Error(overrideResult.message);
        }

        updateData = {
          ...updateData,
          status: 'completed',
          wbc_batch_id: `MANUAL-${wbcReference}`
        };
      }

      // Update payment run in DB
      const { error: updateError } = await supabase
        .from('payment_runs')
        .update(updateData)
        .eq('id', paymentRun.id);

      if (updateError) throw updateError;

      // The 1st approval was audited and the release was not, so the audit trail
      // showed that a payment had been approved but never that it was released.
      await auditService.log(user, useWBC ? 'payment_2nd_released' : 'payment_manual_override', {
        details: {
          payment_run: paymentRun.id,
          deal: paymentRun.deal_id,
          amount: paymentRun.total_amount,
          wbc_batch_id: updateData.wbc_batch_id ?? null,
          manual_reference: useWBC ? null : wbcReference,
        },
      });

      if (onApprovalComplete) {
        onApprovalComplete(paymentRun.id);
      }

      // Reload next payment run
      setTimeout(() => loadPaymentRun(), 1000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Approval failed';
      setError(msg);
      console.error('Approval error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="payment-approval-view">
        <div className="loading">Loading payment approvals...</div>
      </div>
    );
  }

  if (!paymentRun) {
    // A load failure used to render as "All payments are up to date" with no
    // Refresh control — money sitting unreleased behind a reassuring message.
    const failed = !!error && error !== 'No pending approvals';
    return (
      <div className="payment-approval-view">
        <div className="empty-state">
          <h2>{failed ? 'Could not load payment approvals' : 'No Pending Approvals'}</h2>
          <p>{failed ? error : 'All payments are up to date'}</p>
          <button className="btn btn-secondary" onClick={loadPaymentRun}>Refresh</button>
        </div>
      </div>
    );
  }

  const vehicle = paymentRun.deal;
  const vehicleDescription =
    [vehicle?.autograb_year, vehicle?.autograb_make, vehicle?.autograb_model]
      .filter(Boolean)
      .join(' ') || 'Vehicle details unavailable';
  const formattedAmount = `$${paymentRun.total_amount.toLocaleString('en-AU', { minimumFractionDigits: 2 })}`;

  const releaseSummary =
    confirmRelease === 'manual'
      ? `Mark ${formattedAmount} as paid against WBC reference ${wbcReference.trim()}`
      : `Release ${formattedAmount} to Westpac Business Online`;

  return (
    <div className="payment-approval-view">
      {confirmRelease && (
        <div className="release-confirm-overlay" role="alertdialog" onClick={() => setConfirmRelease(null)}>
          <div className="release-confirm" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm payment release</h3>
            <p className="release-confirm-amount">{formattedAmount}</p>
            <ul className="release-confirm-list">
              <li><strong>Vehicle:</strong> {vehicleDescription}</li>
              <li><strong>Seller:</strong> {vehicle?.seller_name || '—'}</li>
              <li><strong>Action:</strong> {releaseSummary}</li>
            </ul>
            <p className="release-confirm-warn">
              This releases funds and cannot be undone from DirectAuto. Are you sure you would like to proceed?
            </p>
            <div className="release-confirm-actions">
              <button className="btn btn-secondary" onClick={() => setConfirmRelease(null)} disabled={submitting}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={submitting}
                onClick={() => {
                  const mode = confirmRelease;
                  setConfirmRelease(null);
                  handleApprove(mode === 'wbc');
                }}
              >
                {submitting ? 'Processing...' : `Yes, release ${formattedAmount}`}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="approval-header">
        <h1>Payment Approval - Step 2/2 (Tuo)</h1>
        <p className="subtitle">Final approval before payment release to Westpac</p>
      </div>

      {notice && (
        <div className="alert alert-success" onClick={() => setNotice(null)}>
          <span>{notice}</span>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
        </div>
      )}

      {/* Deal Summary */}
      <div className="deal-summary card">
        <div className="summary-row">
          <div className="summary-item">
            <label>Vehicle</label>
            <span className="value">{vehicleDescription}</span>
          </div>
          <div className="summary-item">
            <label>Buyer</label>
            <span className="value">{vehicle?.buyer_name}</span>
          </div>
          <div className="summary-item">
            <label>Seller</label>
            <span className="value">{vehicle?.seller_name}</span>
          </div>
        </div>

        <div className="amount-row">
          <div className="amount-item">
            <label>Purchase Price</label>
            <span className="amount">${paymentRun.purchase_price.toFixed(2)}</span>
          </div>
          <div className="amount-item total">
            <label>Total to Pay</label>
            <span className="amount">{formattedAmount}</span>
          </div>
        </div>
      </div>

      {/* Winnie's Approval Status */}
      <div className="approval-status card">
        <h2>Step 1: Winnie's Approval</h2>
        <div className="status-badge approved">
          <svg className="check-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
          </svg>
          <span>Approved</span>
        </div>
        <div className="approval-details">
          <p>
            <strong>Approved by Winnie</strong>
            {paymentRun.winnie_reviewed_at && (
              <>
                <br />
                <span className="timestamp">
                  {new Date(paymentRun.winnie_reviewed_at).toLocaleString('en-AU')}
                </span>
              </>
            )}
          </p>
          <p className="note">All 6 DD checks completed</p>
        </div>
      </div>

      {/* Payment Release Options */}
      <div className="payment-release card">
        <h2>Step 2: Payment Release</h2>

        <div className="release-options">
          {/* Option 1: WBC Release */}
          <div className="option">
            <h3>Option 1: Automatic WBC Release (Recommended)</h3>
            <p>
              Release payment directly to Westpac Business Online. The payment will be processed
              automatically and the deal will be moved to paid status.
            </p>
            <button
              className="btn btn-primary"
              onClick={() => setConfirmRelease('wbc')}
              disabled={submitting}
            >
              {submitting ? 'Processing...' : 'Approve & Release to WBC'}
            </button>
          </div>

          {/* Divider */}
          <div className="divider-or">or</div>

          {/* Option 2: Manual Override */}
          <div className="option">
            <h3>Option 2: Manual Override</h3>
            <p>
              If WBC is unreachable, enter the WBC reference number manually and mark payment as
              complete. Use only if WBC API is down.
            </p>

            {!manualOverride ? (
              <button
                className="btn btn-secondary"
                onClick={() => setManualOverride(true)}
                disabled={submitting}
              >
                Use Manual Override
              </button>
            ) : (
              <div className="manual-form">
                <input
                  type="text"
                  placeholder="Enter WBC reference number (e.g., WBC-PAY-12345)"
                  value={wbcReference}
                  onChange={e => setWbcReference(e.target.value)}
                  disabled={submitting}
                  className="input"
                />
                <div className="manual-actions">
                  <button
                    className="btn btn-primary"
                    onClick={() => setConfirmRelease('manual')}
                    disabled={!wbcReference.trim() || submitting}
                  >
                    {submitting ? 'Processing...' : 'Confirm Manual Override'}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setManualOverride(false);
                      setWbcReference('');
                    }}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <PaymentHoldControls
        runId={paymentRun.id}
        stage="second_release"
        user={user}
        allowSendBack
        disabled={submitting}
        onDone={(msg) => {
          setError(null);
          setNotice(msg);
          loadPaymentRun();
        }}
      />

      {/* Framework C.4 Notes */}
      <div className="framework-notes card">
        <h3>Framework C.4 - Payment Workflow</h3>
        <ul>
          <li><strong>Step 1 (Winnie):</strong> Completed - All 6 DD checks approved</li>
          <li><strong>Step 2 (Tuo - You):</strong> Approve and release payment to Westpac</li>
          <li><strong>Step 3 (WBC):</strong> Process batch, move deal to paid</li>
          <li><strong>Critical Requirement:</strong> Initiator ≠ 1st approver ≠ 2nd releaser (3 distinct users, all three now enforced)</li>
          <li><strong>If something is wrong:</strong> hold it, send it back for re-checking, or reject it — never approve around a problem.</li>
        </ul>
      </div>

      {/* Actions */}
      <div className="actions">
        <button
          className="btn btn-secondary"
          onClick={loadPaymentRun}
          disabled={submitting}
        >
          Refresh
        </button>
      </div>
    </div>
  );
};

export default PaymentApprovalView;
